import React, { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Phone, Mail, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { SectionLabel } from "./Shared";
import { submitContact } from "../lib/api";
import { CONTACT_DETAILS, SERVICES } from "../config/siteConfig";
import { viewportOnce } from "../lib/motion";

const inputCls =
  "w-full bg-white border border-black/10 rounded-sm px-4 py-3.5 font-body text-[#111] placeholder-black/35 focus:outline-none focus:border-[#C62828] transition-colors";

export const Contact = () => {
  const [form, setForm] = useState({
    full_name: "", company_name: "", email: "", phone: "", service_required: "", message: "",
  });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Thank you — we'll respond within one business day.");
      setForm({ full_name: "", company_name: "", email: "", phone: "", service_required: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const details = [
    { icon: MapPin, label: "Visit", value: CONTACT_DETAILS.location },
    { icon: Phone, label: "Call", value: CONTACT_DETAILS.phone, sub: CONTACT_DETAILS.hours },
    { icon: Mail, label: "Email", value: CONTACT_DETAILS.email, sub: CONTACT_DETAILS.emailNote },
  ];

  return (
    <section id="contact" className="bg-[#F7F7F5] py-24 md:py-36" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 grid lg:grid-cols-2 gap-14 lg:gap-20">
        <div>
          <SectionLabel>Get In Touch</SectionLabel>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.03] tracking-tight text-[#111111]">
            UAE | India | <span className="text-[#C62828]">Latin America.</span>
          </h2>
          <p className="font-body text-[#555] text-lg leading-relaxed mt-8 max-w-lg">
            Tell us about your project — application development, product engineering, consulting or managed
            websites. Our team will be in touch shortly.
          </p>

          <div className="mt-12 space-y-6">
            {details.map((d) => (
              <div key={d.label} className="flex items-center gap-5" data-testid={`contact-detail-${d.label.toLowerCase()}`}>
                <span className="w-12 h-12 rounded-sm bg-white border border-black/10 flex items-center justify-center">
                  <d.icon size={20} className="text-[#C62828]" strokeWidth={1.6} />
                </span>
                <div>
                  <p className="overline text-[10px] text-[#999]">{d.label}</p>
                  <p className="font-body text-[#111] font-500">{d.value}</p>
                  {d.sub && <p className="font-body text-xs text-[#888] mt-0.5">{d.sub}</p>}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-10 overflow-hidden rounded-sm border border-black/10">
            <iframe
              title="INRISOFT location — Hyderabad"
              src={CONTACT_DETAILS.mapEmbed}
              width="100%"
              height="220"
              style={{ border: 0 }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              data-testid="contact-map"
            />
          </div>
        </div>

        <motion.form
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={viewportOnce}
          transition={{ duration: 0.7 }}
          data-testid="contact-form"
          className="bg-white p-8 md:p-10 rounded-sm border border-black/10 space-y-4 h-fit"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-name" className={inputCls} placeholder="Full Name *" value={form.full_name} onChange={set("full_name")} />
            <input data-testid="contact-company" className={inputCls} placeholder="Company Name" value={form.company_name} onChange={set("company_name")} />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email Address *" value={form.email} onChange={set("email")} />
            <input data-testid="contact-phone" className={inputCls} placeholder="Phone Number" value={form.phone} onChange={set("phone")} />
          </div>
          <select data-testid="contact-service" className={`${inputCls} text-[#111]`} value={form.service_required} onChange={set("service_required")}>
            <option value="">Service Required</option>
            {SERVICES.map((s) => (
              <option key={s.id} value={s.title}>{s.title}</option>
            ))}
          </select>
          <textarea data-testid="contact-message" rows={5} className={inputCls} placeholder="Message *" value={form.message} onChange={set("message")} />
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="group w-full inline-flex items-center justify-center gap-2 bg-[#C62828] text-white font-accent uppercase tracking-widest text-sm px-8 py-4 rounded-sm hover:bg-[#111111] transition-colors duration-400 disabled:opacity-60"
          >
            {loading ? <Loader2 size={17} className="animate-spin" /> : <>Send Message <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" /></>}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
